import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import ProductDetailPage from './pages/ProductDetailsPage';
import FAQs from './pages/FAQs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { Analytics } from "@vercel/analytics/react";

// The Vercel Insights script only exists on actual Vercel infrastructure, so
// avoid requesting it when running locally (npm run dev / vite preview),
// where it would always 404.
const isLocalHost = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title || 'RK and K',
      });
    }
  }, [location]);

  return null;
}

function App() {
  // Sticky footer reveal: the footer is pinned to the viewport bottom via
  // `fixed`, hidden underneath the (higher z-index, opaque) page content
  // above it. A spacer the exact height of the footer reserves the scroll
  // room needed for the page content's trailing edge to scroll up and off,
  // progressively revealing the footer beneath it —
  // https://blog.olivierlarose.com/demos/sticky-footer
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const updateHeight = () => setFooterHeight(el.offsetHeight);
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Router>
      <AnalyticsTracker />
      <Analytics mode={isLocalHost ? 'development' : 'production'} />

      <div className="relative z-10 bg-white dark:bg-[#121212]">
        <Navbar />

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/details" element={<ProductDetailPage />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </div>

      <div style={{ height: footerHeight }} aria-hidden="true" />

      <div ref={footerRef} className="fixed bottom-0 left-0 w-full z-0">
        <Footer />
      </div>
    </Router>
  );
}

export default App;
